import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import {
    getEditorTableData,
    getAvailableUsers,
    getEditorTableDataRPC
} from "../../lib/supabase/schedule";
import {
    subscribeToJobUpdates,
    unsubscribeFromJobUpdates
} from "../../lib/supabase/realtime";
import { supabase } from "../../config/supabase";
import JobEditorTable from "./components/JobEditorTable";
import { FiGrid, FiArrowLeft, FiPlusSquare } from "react-icons/fi";
import BackButton from "../common/BackButton";

const EditorSchedule = () => {
    const { userProfile, company } = useAuth();
    const navigate = useNavigate();
    const { companySlug } = useParams();

    const [editorData, setEditorData] = useState([]);
    // Ref to JobEditorTable — used to call patchJob() imperatively for subscription
    // UPDATE events so the parent (this component) never re-renders on every DB change.
    const jobTableRef = useRef(null);
    const [availableUsers, setAvailableUsers] = useState({ designers: [], estimators: [] });
    const [isLoading, setIsLoading] = useState(false);

    const [showOnlyMine, setShowOnlyMine] = useState(() => {
        try { return JSON.parse(localStorage.getItem('jobEditor_showOnlyMine') ?? 'false'); } catch { return false; }
    });

    const [selectedUser, setSelectedUser] = useState(() => {
        try { return localStorage.getItem('jobEditor_selectedUser') ?? ''; } catch { return ''; }
    });

    // Load sortBy from localStorage with default value 'newest'
    const [sortBy, setSortBy] = useState(() => {
        try {
            const stored = localStorage.getItem('editor_sortBy');
            return stored ? JSON.parse(stored) : 'newest';
        } catch (error) {
            console.warn('Failed to load sortBy from localStorage:', error);
            return 'newest';
        }
    });

    const [cancelledFilter, setCancelledFilter] = useState(() => {
        try {
            return localStorage.getItem('jobEditor_cancelledFilter') || 'active_only';
        } catch {
            return 'active_only';
        }
    });

    // Load jobTypeFilter from localStorage
    const [jobTypeFilter, setJobTypeFilter] = useState(() => {
        try {
            const stored = localStorage.getItem('jobEditor_jobTypeFilter');
            return stored ? JSON.parse(stored) : 'all';
        } catch (error) {
            console.warn('Failed to load jobTypeFilter from localStorage:', error);
            return 'all';
        }
    });

    const [urgentFilter, setUrgentFilter] = useState(() => {
        try {
            const stored = localStorage.getItem('jobEditor_urgentFilter');
            return stored ? JSON.parse(stored) : 'all';
        } catch (error) {
            return 'all';
        }
    });

    const [quoteSentFilter, setQuoteSentFilter] = useState(() => {
        try {
            const stored = localStorage.getItem('jobEditor_quoteSentFilter');
            return stored ? JSON.parse(stored) : 'all';
        } catch (error) {
            return 'all';
        }
    });

    const [hideEstimationCompleted, setHideEstimationCompleted] = useState(() => {
        try {
            const stored = localStorage.getItem('jobEditor_hideEstimationCompleted');
            return stored ? JSON.parse(stored) : false;
        } catch (error) {
            return false;
        }
    });

    const [hideEstimatorAssigned, setHideEstimatorAssigned] = useState(() => {
        try {
            const stored = localStorage.getItem('jobEditor_hideEstimatorAssigned');
            return stored ? JSON.parse(stored) : false;
        } catch (error) {
            return false;
        }
    });

    // Quote Types + Invoice filters are applied server-side (in the RPC), so they live
    // here to trigger a refetch. localStorage keys are unchanged from when JobEditorTable owned them.
    const [selectedQuoteTypes, setSelectedQuoteTypes] = useState(() => {
        const defaults = {
            roof: true,
            wall: true,
            floor: true,
            estimation: true,
            timber: false,
            beams: true,
            flooring_sheets: true,
        };
        try {
            const stored = localStorage.getItem('jobEditor_selectedQuoteTypes');
            return stored ? JSON.parse(stored) : defaults;
        } catch (error) {
            return defaults;
        }
    });

    const [invoiceFilter, setInvoiceFilter] = useState(() => {
        try {
            const stored = localStorage.getItem('jobEditor_invoiceFilter');
            return stored ? JSON.parse(stored) : 'all';
        } catch (error) {
            return 'all';
        }
    });

    const [editorPagination, setEditorPagination] = useState({
        page: 1,
        limit: 50,
        total: 0,
        hasMore: true
    });
    const [isLoadingMoreEditor, setIsLoadingMoreEditor] = useState(false);

    useEffect(() => {
        if (company?.id) {
            fetchData();
        }
    }, [company, showOnlyMine, selectedUser, sortBy, jobTypeFilter, urgentFilter, quoteSentFilter, hideEstimationCompleted, hideEstimatorAssigned, cancelledFilter, selectedQuoteTypes, invoiceFilter]);

    useEffect(() => {
        if (userProfile?.role === 'admin' || userProfile?.role === 'super_admin') {
            fetchAvailableUsers();
        }
    }, [userProfile, company]);

    // Save sortBy to localStorage whenever it changes
    useEffect(() => {
        try {
            localStorage.setItem('editor_sortBy', JSON.stringify(sortBy));
        } catch (error) {
            console.warn('Failed to save sortBy to localStorage:', error);
        }
    }, [sortBy]);

    // Save jobTypeFilter to localStorage whenever it changes
    useEffect(() => {
        try {
            localStorage.setItem('jobEditor_jobTypeFilter', JSON.stringify(jobTypeFilter));
        } catch (error) {
            console.warn('Failed to save jobTypeFilter to localStorage:', error);
        }
    }, [jobTypeFilter]);

    useEffect(() => {
        try { localStorage.setItem('jobEditor_urgentFilter', JSON.stringify(urgentFilter)); } catch (e) { }
    }, [urgentFilter]);

    useEffect(() => {
        try { localStorage.setItem('jobEditor_quoteSentFilter', JSON.stringify(quoteSentFilter)); } catch (e) { }
    }, [quoteSentFilter]);

    useEffect(() => {
        try { localStorage.setItem('jobEditor_hideEstimationCompleted', JSON.stringify(hideEstimationCompleted)); } catch (e) { }
    }, [hideEstimationCompleted]);

    useEffect(() => {
        try { localStorage.setItem('jobEditor_hideEstimatorAssigned', JSON.stringify(hideEstimatorAssigned)); } catch (e) { }
    }, [hideEstimatorAssigned]);

    useEffect(() => {
        try { localStorage.setItem('jobEditor_showOnlyMine', JSON.stringify(showOnlyMine)); } catch (e) { }
    }, [showOnlyMine]);

    useEffect(() => {
        try { localStorage.setItem('jobEditor_selectedUser', selectedUser); } catch (e) { }
    }, [selectedUser]);

    useEffect(() => {
        try {
            localStorage.setItem('jobEditor_cancelledFilter', cancelledFilter);
        } catch (e) { }
    }, [cancelledFilter]);

    useEffect(() => {
        try { localStorage.setItem('jobEditor_selectedQuoteTypes', JSON.stringify(selectedQuoteTypes)); } catch (e) { }
    }, [selectedQuoteTypes]);

    useEffect(() => {
        try { localStorage.setItem('jobEditor_invoiceFilter', JSON.stringify(invoiceFilter)); } catch (e) { }
    }, [invoiceFilter]);

    const getEnabledQuoteTypes = () =>
        Object.keys(selectedQuoteTypes).filter(key => selectedQuoteTypes[key]);

    const fetchAvailableUsers = async () => {
        if (!company?.id) return;

        try {
            const users = await getAvailableUsers(company.id);
            setAvailableUsers(users);
        } catch (error) {
            console.error("Failed to fetch users:", error);
        }
    };

    const fetchData = async (loadMore = false) => {
        if (!company?.id) return;

        try {
            if (!loadMore) {
                setIsLoading(true);
            }

            const currentPage = loadMore ? editorPagination.page + 1 : 1;

            const filters = {
                userId: showOnlyMine ? userProfile?.id : selectedUser || null,
                sortBy: sortBy,
                jobTypeFilter: jobTypeFilter,
                urgentFilter: urgentFilter,
                quoteSentFilter: quoteSentFilter,
                hideEstimationCompleted: hideEstimationCompleted,
                hideEstimatorAssigned: hideEstimatorAssigned,
                cancelledFilter,
                quoteTypes: getEnabledQuoteTypes(),
                invoiceFilter,
            };

            if (loadMore) setIsLoadingMoreEditor(true);

            const result = await getEditorTableDataRPC(
                company.id,
                'editor',
                currentPage,
                editorPagination.limit,
                filters
            );

            if (loadMore) {
                setEditorData(prev => [...prev, ...result.data]);
            } else {
                setEditorData(result.data);
            }

            setEditorPagination(result.pagination);

            if (loadMore) setIsLoadingMoreEditor(false);
        } catch (error) {
            console.error("Error fetching editor data:", error);
        } finally {
            if (!loadMore) {
                setIsLoading(false);
            }
        }
    };

    const loadMoreEditorAssignments = () => {
        if (editorPagination.hasMore && !isLoadingMoreEditor) {
            fetchData(true);
        }
    };

    // Real-time subscriptions
    useEffect(() => {
        if (!company?.id) return;

        // Helper: quotes are keyed by UUID (not by type string). Find the matching
        // entry by the quote_type field inside the value and update it in-place.
        // Falls back to direct type-string key for old-style data.
        const patchQuotesByType = (quotesObj, quoteType, updater) => {
            if (!quotesObj) return quotesObj;
            const result = { ...quotesObj };
            let patched = false;
            for (const [key, q] of Object.entries(result)) {
                if (q && (q.quote_type === quoteType || q.quoteType === quoteType)) {
                    result[key] = updater(q);
                    patched = true;
                }
            }
            // Fallback: old-style direct type-string key
            if (!patched && result[quoteType]) {
                result[quoteType] = updater(result[quoteType]);
            }
            return result;
        };

        // Handle job table updates
        // UPDATE: patch the specific row inside JobEditorTable directly — no parent re-render.
        // INSERT/DELETE: legitimately change the list, so full setEditorData is acceptable.
        const handleJobUpdate = (payload) => {
            if (payload.eventType === 'DELETE') {
                jobTableRef.current?.removeJob(payload.old.id);
                return;
            }

            if (payload.eventType === 'INSERT') {
                fetchSingleJob(payload.new.id);
                return;
            }

            if (payload.eventType === 'UPDATE') {
                if (payload.old?.is_cancelled !== payload.new?.is_cancelled) {
                    fetchSingleJob(payload.new.id); // not fetchData()
                    return;
                }

                jobTableRef.current?.patchJob(payload.new.id, row => ({
                    ...row,
                    is_cancelled: payload.new.is_cancelled,
                    isCancelled: payload.new.is_cancelled,

                    uniqueId: payload.new.unique_id,
                    customerName: payload.new.customer_name,
                    customerNumber: payload.new.customer_number,
                    customerMail: payload.new.customer_mail,
                    carpenterSupervisorName: payload.new.carpenter_supervisor_name,
                    carpenterSupervisorNumber: payload.new.carpenter_supervisor_number,
                    customerAddress: payload.new.customer_address,
                    jobType: payload.new.job_type,
                    mainStatus: payload.new.main_status,
                    currentStatus: payload.new.current_status,
                    invoiced: payload.new.invoiced,
                    quoteSent: payload.new.quote_sent,
                    bookingDeliveryDate: payload.new.booking_delivery_date,
                    expectedEstimationFinishDate: payload.new.expected_estimation_finish_date,
                    updatedAt: payload.new.updated_at,
                    on_hold: payload.new?.on_hold,
                    jobInformation: {
                        roofType: payload.new.roof_type,
                        roofTrussCentres: payload.new.roof_truss_centres,
                        isSingleStorey: payload.new.is_single_storey,
                        isUnit: payload.new.is_unit,
                        unitCount: payload.new.unit_count,
                        urgentQuote: payload.new.urgent_quote,
                        urgentDesign: payload.new.urgent_design,
                        siteMeasureRequired: payload.new.site_measure_required,
                        siteMeasureWall: payload.new.site_measure_wall,
                        siteMeasureFloor: payload.new.site_measure_floor,
                        siteMeasureTruss: payload.new.site_measure_truss
                    }
                }));
            }
        };

        // Handle job_designers table updates
        const handleDesignersUpdate = (payload) => {
            const { new: newData, old: oldData } = payload;

            if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
                const newDesignStatus = newData.is_completed ? 'completed'
                    : newData.is_started ? 'in_progress' : 'waiting_assignment';
                jobTableRef.current?.patchJob(newData.job_id, row => ({
                    ...row,
                    quotes: patchQuotesByType(row.quotes, newData.quote_type, q => ({
                        ...q,
                        designers: [{ userId: newData.user_id, name: newData.name }],
                        designStatus: newDesignStatus,
                        design_status: newDesignStatus,
                    })),
                }));
            }

            if (payload.eventType === 'DELETE') {
                jobTableRef.current?.patchJob(oldData.job_id, row => ({
                    ...row,
                    quotes: patchQuotesByType(row.quotes, oldData.quote_type, q => ({
                        ...q,
                        designers: [],
                        designStatus: 'waiting_assignment',
                        design_status: 'waiting_assignment',
                    })),
                }));
            }
        };

        // Handle job_estimators table updates
        const handleEstimatorsUpdate = (payload) => {
            const { new: newData, old: oldData } = payload;

            if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
                jobTableRef.current?.patchJob(newData.job_id, row => ({
                    ...row,
                    estimator: {
                        user_id: newData.user_id,
                        name: newData.name,
                        is_completed: newData.is_completed,
                        is_started: newData.is_started,
                    },
                }));
            }

            if (payload.eventType === 'DELETE') {
                jobTableRef.current?.patchJob(oldData.job_id, row => ({
                    ...row,
                    estimator: null,
                }));
            }
        };

        // Handle job_quotes table updates
        const handleQuotesUpdate = (payload) => {
            const { new: newData } = payload;

            if (payload.eventType === 'UPDATE' || payload.eventType === 'INSERT') {
                jobTableRef.current?.patchJob(newData.job_id, row => ({
                    ...row,
                    quotes: {
                        ...row.quotes,
                        [newData.id]: {
                            ...row.quotes?.[newData.id],
                            ...newData,
                            isRequired: newData.is_required,
                            is_required: newData.is_required,
                            designStatus: newData.design_status,
                            design_status: newData.design_status,
                            estimationStatus: newData.estimation_status,
                            estimation_status: newData.estimation_status,
                            invoice_state: newData.invoice_state,
                            xero_invoice_status: newData.xero_invoice_status,
                            xero_invoice_id: newData.xero_invoice_id,
                            on_hold: newData.on_hold,
                        }
                    }
                }));
            }
        };

        const getCurrentFilters = () => ({
            userId: showOnlyMine ? userProfile?.id : selectedUser || null,
            sortBy,
            jobTypeFilter,
            urgentFilter,
            quoteSentFilter,
            hideEstimationCompleted,
            hideEstimatorAssigned,
            cancelledFilter,
            quoteTypes: getEnabledQuoteTypes(),
            invoiceFilter,
        });

        const fetchSingleJob = async (jobId) => {
            try {
                const result = await getEditorTableDataRPC(
                    company.id,
                    'editor',
                    1,
                    1,
                    {
                        ...getCurrentFilters(),
                        jobId,
                    }
                );

                const job = result.data?.[0];

                if (job) {
                    jobTableRef.current?.upsertJob(job);
                } else {
                    jobTableRef.current?.removeJob(jobId);
                }
            } catch (error) {
                console.error('Failed to fetch single job:', error);
            }
        };

        // Subscribe to all relevant tables
        const jobSubscription = subscribeToJobUpdates(company.id, handleJobUpdate);

        // Subscribe to job_designers
        const designersChannel = supabase
            .channel(`designers-changes-${company.id}`)
            .on(
                'postgres_changes',
                {
                    event: '*',
                    schema: 'public',
                    table: 'job_designers'
                },
                handleDesignersUpdate
            )
            .subscribe();

        // Subscribe to job_estimators
        const estimatorsChannel = supabase
            .channel(`estimators-changes-${company.id}`)
            .on(
                'postgres_changes',
                {
                    event: '*',
                    schema: 'public',
                    table: 'job_estimators'
                },
                handleEstimatorsUpdate
            )
            .subscribe();

        // Subscribe to job_quotes
        const quotesChannel = supabase
            .channel(`quotes-changes-${company.id}`)
            .on(
                'postgres_changes',
                {
                    event: '*',
                    schema: 'public',
                    table: 'job_quotes'
                },
                handleQuotesUpdate
            )
            .subscribe();

        return () => {
            unsubscribeFromJobUpdates(jobSubscription);
            supabase.removeChannel(designersChannel);
            supabase.removeChannel(estimatorsChannel);
            supabase.removeChannel(quotesChannel);
        };
    }, [
        company?.id,
        cancelledFilter,
        showOnlyMine,
        selectedUser,
        sortBy,
        jobTypeFilter,
        urgentFilter,
        quoteSentFilter,
        hideEstimationCompleted,
        hideEstimatorAssigned,
        selectedQuoteTypes,
        invoiceFilter
    ]);

    return (
        <div className="editor-page contents">
            {/* Header */}
            <div className="editor-page-header flex flex-col mb-6 gap-4 items-start">

                <BackButton
                    text="Back to Schedule Hub"
                    variant="ghost"
                    size="sm"
                    iconType="chevron"
                    className="text-blue-600 hover:text-blue-800"
                />
                <div className="flex flex-row justify-between w-full">
                    <div className="flex items-center gap-3">
                        <div className="p-3 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg">
                            <FiGrid className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900">Job Editor</h1>
                            <p className="text-gray-600 mt-1">
                                Comprehensive view and management of all construction jobs
                            </p>
                        </div>
                    </div>
                    <div>
                        <a
                            href={`https://app.notifyapp.com.au/${companySlug}/assignment/new`}
                            target="_blank"
                            rel="noreferrer noopener"
                            className="bg-blue-600 flex flex-row gap-2 font-semibold text-white px-4 py-3 self-start rounded-md hover:bg-blue-700 transition-colors"
                        >
                            <FiPlusSquare className="self-center w-6 h-6" /> New Assignment</a>
                    </div>
                </div>
            </div>

            {/* User Filters */}
            <div className="editor-user-filters bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
                <div className="flex flex-col sm:flex-row gap-4">
                    <div className="flex items-center">
                        <input
                            type="checkbox"
                            id="showOnlyMine"
                            checked={showOnlyMine}
                            onChange={(e) => {
                                setShowOnlyMine(e.target.checked);
                                if (e.target.checked) {
                                    setSelectedUser('');
                                }
                            }}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor="showOnlyMine" className="ml-2 text-sm text-gray-700 font-medium">
                            Show only my assignments
                        </label>
                    </div>

                    {(userProfile?.role === 'admin' || userProfile?.role === 'super_admin') && (
                        <div className="flex items-center gap-2">
                            <label className="text-sm text-gray-700 font-medium">Filter by user:</label>
                            <select
                                value={selectedUser}
                                onChange={(e) => {
                                    setSelectedUser(e.target.value);
                                    if (e.target.value) {
                                        setShowOnlyMine(false);
                                    }
                                }}
                                className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-blue-500 focus:border-blue-500 min-w-[200px] bg-white"
                            >
                                <option value="">All users</option>

                                {availableUsers.designers && availableUsers.designers.length > 0 && (
                                    <optgroup label="Designers">
                                        {availableUsers.designers.map(user => (
                                            <option key={user.userId} value={user.userId}>
                                                {user.username}
                                            </option>
                                        ))}
                                    </optgroup>
                                )}

                                {availableUsers.estimators && availableUsers.estimators.length > 0 && (
                                    <optgroup label="Estimators">
                                        {availableUsers.estimators.map(user => (
                                            <option key={user.userId} value={user.userId}>
                                                {user.username}
                                            </option>
                                        ))}
                                    </optgroup>
                                )}
                            </select>
                        </div>
                    )}
                </div>
            </div>

            {/* Job Editor Table */}
            <div className="editor-table-panel bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <JobEditorTable
                    ref={jobTableRef}
                    assignments={editorData}
                    userType={userProfile?.role}
                    company={company}
                    invoiceCompany={company}
                    companyId={company?.id}
                    navigate={navigate}
                    onLoadMore={loadMoreEditorAssignments}
                    hasMore={editorPagination.hasMore}
                    isLoadingMore={isLoadingMoreEditor}
                    isLoading={isLoading}
                    availableDesigners={availableUsers.designers || []}
                    scheduleType="editor"
                    showOnlyMine={showOnlyMine}
                    sortBy={sortBy}
                    setSortBy={setSortBy}
                    selectedUser={selectedUser}
                    jobTypeFilter={jobTypeFilter}
                    setJobTypeFilter={setJobTypeFilter}
                    urgentFilter={urgentFilter}
                    setUrgentFilter={setUrgentFilter}
                    quoteSentFilter={quoteSentFilter}
                    setQuoteSentFilter={setQuoteSentFilter}
                    hideEstimationCompleted={hideEstimationCompleted}
                    setHideEstimationCompleted={setHideEstimationCompleted}
                    hideEstimatorAssigned={hideEstimatorAssigned}
                    setHideEstimatorAssigned={setHideEstimatorAssigned}
                    onDuplicateJob={fetchData}
                    onRefresh={fetchData}
                    cancelledFilter={cancelledFilter}
                    setCancelledFilter={setCancelledFilter}
                    selectedQuoteTypes={selectedQuoteTypes}
                    setSelectedQuoteTypes={setSelectedQuoteTypes}
                    invoiceFilter={invoiceFilter}
                    setInvoiceFilter={setInvoiceFilter}
                />
            </div>
        </div>
    );
};

export default EditorSchedule;